import { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslations } from 'next-intl';

import { CompleteQuestTaskCard } from '@zealy/design-system';
import { useBasicQuestInfo, useHasClaimedQuest } from '@zealy/queries';

import { useError } from '#app/cw/[subdomain]/(app)/questboard/admin/[questId]/_components/FormError.context';
import { TASK_CONFIG } from '#constants/quests/Task.constants';

import type { QuestTask } from '../Tasks.types';
import { withQuest } from '../Tasks.funcs';

type WrapperProps = Extract<QuestTask, { type: 'partnershipQuest' }>;

const type = 'partnershipQuest' as const;
type Task = typeof type;

export const PartnershipQuest = ({
  task,
  preview = false,
  index,
}: { task: WrapperProps } & { preview?: boolean; index?: number }) => {
  const questId = task.settings.questUrl?.split('?')[0]?.split('/').pop();
  const quest = useBasicQuestInfo(questId || '_');
  const hasClaimedQuest = useHasClaimedQuest(questId);

  const t = useTranslations('quest.type.partnershipQuest');
  const { formState } = useFormContext();

  const { clearError, error, setError } = useError(
    preview && typeof index === 'number' ? `tasks.${index}.settings.questUrl` : `${task.id}.role`,
    !preview && task.id && !hasClaimedQuest.data?.hasClaimed ? 'Quest Not Claimed' : undefined,
  );

  useEffect(() => {
    if (!task.id) return;

    if (error && hasClaimedQuest.data?.hasClaimed) {
      clearError();
    } else if (!error && !hasClaimedQuest.data?.hasClaimed && !preview) {
      setError('Quest Not Claimed');
    }
  }, [hasClaimedQuest.data, task.id, setError, error, clearError, preview]);

  const isDisabled = !quest.data?.id || !quest.data?.subdomain || hasClaimedQuest.data?.hasClaimed;

  const openQuest = (questId: string) => {
    window.open(`/embed/c/${quest.data?.subdomain}/${questId}`, '_blank');
  };

  const description = quest.data?.communityName
    ? `Claim "${quest.data?.name}" in the community${quest.data?.communityName}`
    : '';

  return (
    <CompleteQuestTaskCard
      type="partnershipQuest"
      title={t('label')}
      imageUrl={quest.data?.communityImage ?? undefined}
      description={description}
      icon={TASK_CONFIG.partnershipQuest.icon}
      name={quest.data?.communityName ?? ''}
      buttonProps={{
        children: hasClaimedQuest.data?.hasClaimed ? 'Claimed' : 'Claim Quest',
        isDisabled,
        className:
          'w-full bg-task-type-partnership-quest hover:bg-task-type-partnership-quest-hover active:bg-task-type-partnership-quest-press',
        onClick: () => openQuest(quest.data?.id ?? ''),
      }}
      error={
        formState.errors?.[task.id]?.message
          ? String(formState.errors?.[task.id]?.message)
          : undefined
      }
    />
  );
};
const PartnershipQuestClaim = withQuest<WrapperProps, Task>(({ task }) => {
  const { field } = useController({
    name: task.id,
    defaultValue: {
      taskId: task.id,
      type,
    },
  });

  return <PartnershipQuest task={task} />;
});

export default PartnershipQuestClaim;
